import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import type { SupabaseClient } from '@supabase/supabase-js'
import { useEffect } from 'react'
import { exchangeOAuthCallbackUrl } from '../oauthCallback'

export type UseNativeOAuthCallbackOptions = {
  /** Defaults to true on native platforms only */
  enabled?: boolean
  onError?: (message: string) => void
  closeBrowser?: () => Promise<void>
}

/**
 * Listens for `stepcare://auth-callback` (or similar) on Capacitor native builds.
 */
export function useNativeOAuthCallback(
  supabase: SupabaseClient,
  options: UseNativeOAuthCallbackOptions = {}
) {
  const { enabled = Capacitor.isNativePlatform(), onError, closeBrowser } =
    options

  useEffect(() => {
    if (!enabled) return

    let remove: (() => void) | undefined

    const setup = async () => {
      const listener = await App.addListener('appUrlOpen', async ({ url }) => {
        if (!url.includes('auth-callback')) return

        if (closeBrowser) {
          await closeBrowser().catch(() => undefined)
        }

        const { error } = await exchangeOAuthCallbackUrl(supabase, url)
        if (error) {
          onError?.(error.message)
        }
      })

      remove = () => listener.remove()
    }

    void setup()

    return () => {
      remove?.()
    }
  }, [supabase, enabled, onError, closeBrowser])
}
